import { useCallback } from "react";
import {
  LabelsFilter,
  QueryOperator,
  TagsFilter,
  VqlQuery,
  VqlQueryBase,
} from "types";
import {
  createAndUpdateLabelsFilter,
  createAndUpdateUserTagsFilter,
  isVqlQuery,
} from "helpers/utility/vqlHelpers";
import type { VqlListMutators } from "hooks/useVqlParams";
import { useUserTagsData } from "../../DataPageFilters/UserTagsQueryModal/useUserTagsData";

export function selectedLabelTextsFromVql(vqlQueries: VqlQuery[]): string[] {
  const out: string[] = [];
  for (const q of vqlQueries) {
    if (
      isVqlQuery(q, "labels") &&
      q.labels.op === "is" &&
      Array.isArray(q.labels.value)
    ) {
      out.push(...q.labels.value.map(String));
    }
  }
  return out;
}

export function selectedUserTagIdsFromVql(vqlQueries: VqlQuery[]): string[] {
  const out: string[] = [];
  for (const q of vqlQueries) {
    if (
      isVqlQuery(q, "tags") &&
      q.tags.op === "is" &&
      Array.isArray(q.tags.value)
    ) {
      out.push(...q.tags.value.map(String));
    }
  }
  return out;
}

export function selectedUserTagTextsFromVql(
  vqlQueries: VqlQuery[],
  userTags: ReadonlyArray<{ id: string; text: string }>,
): string[] {
  const ids = selectedUserTagIdsFromVql(vqlQueries);
  const idToText = new Map(userTags.map((t) => [t.id, t.text] as const));
  const seenText = new Set<string>();
  for (const id of ids) {
    const text = idToText.get(id);
    if (text !== undefined) {
      seenText.add(text);
    }
  }
  return Array.from(seenText);
}

export function vqlHasUserTagInIsFilter(
  vqlQueries: VqlQuery[],
  tagId: string,
): boolean {
  return vqlQueries.some(
    (query) =>
      isVqlQuery(query, "tags") &&
      query.tags.op === "is" &&
      Array.isArray(query.tags.value) &&
      query.tags.value.map(String).includes(tagId),
  );
}

export function toggleVqlLabelsIsValue(
  value: string,
  isSelected: boolean,
  vqlQueries: VqlQuery[],
  { addVqlQuery, updateVqlQuery, removeVqlQuery }: VqlListMutators,
): void {
  if (!isSelected) {
    addVqlQuery(createAndUpdateLabelsFilter([value], QueryOperator.IS));
    return;
  }
  const labelQuery = vqlQueries.find(
    (query): query is VqlQueryBase & LabelsFilter =>
      isVqlQuery(query, "labels") &&
      query.labels.op === "is" &&
      query.labels.value.includes(value),
  );
  if (!labelQuery) return;
  const updatedValues = labelQuery.labels.value.filter(
    (label: string) => label !== value,
  );
  if (updatedValues.length > 0) {
    updateVqlQuery({
      ...labelQuery,
      labels: {
        ...labelQuery.labels,
        value: updatedValues,
      },
    });
  } else {
    removeVqlQuery(labelQuery.id);
  }
}

export function toggleVqlUserTagsIsValue(
  tagId: string,
  isSelected: boolean,
  vqlQueries: VqlQuery[],
  { addVqlQuery, updateVqlQuery, removeVqlQuery }: VqlListMutators,
): void {
  if (!isSelected) {
    addVqlQuery(createAndUpdateUserTagsFilter([tagId], QueryOperator.IS));
    return;
  }
  const tagsQuery = vqlQueries.find(
    (query): query is VqlQueryBase & TagsFilter =>
      isVqlQuery(query, "tags") &&
      query.tags.op === "is" &&
      query.tags.value.map(String).includes(tagId),
  );
  if (!tagsQuery) return;
  const updatedValues = tagsQuery.tags.value.filter(
    (tag: string) => String(tag) !== tagId,
  );
  if (updatedValues.length > 0) {
    updateVqlQuery({
      ...tagsQuery,
      tags: {
        ...tagsQuery.tags,
        value: updatedValues,
      },
    });
  } else {
    removeVqlQuery(tagsQuery.id);
  }
}

function tagNameMatches(
  tag: { id: string; text: string },
  tagName: string,
): boolean {
  return (
    tag.id !== "untagged" &&
    tag.text.localeCompare(tagName, undefined, {
      sensitivity: "accent",
    }) === 0
  );
}

export function useCardFilters(fetchTagsCatalog = true) {
  const { userTags } = useUserTagsData(fetchTagsCatalog);

  const resolveUserTagIdByName = useCallback(
    (tagName: string): string | undefined => {
      return userTags.find((t) => tagNameMatches(t, tagName))?.id;
    },
    [userTags],
  );

  const userTagIdByName = useCallback(
    async (tagName: string): Promise<string | undefined> => {
      return resolveUserTagIdByName(tagName);
    },
    [resolveUserTagIdByName],
  );

  return { userTagIdByName, userTags, resolveUserTagIdByName };
}
